/**
 * W3C-style annotation anchors: text-position plus text-quote.
 *
 * A mark on clause 19.4 says "these words are the problem". If it ever drifts
 * to different words, it does not become slightly wrong — it becomes evidence
 * for a claim about a provision nobody objected to. So the rule here matches
 * the one in refs.ts: resolve exactly, or refuse. An anchor that cannot be
 * placed with confidence is reported as orphaned and shown as orphaned. It is
 * never quietly moved somewhere plausible.
 *
 * Two selectors, and both are needed:
 *
 *   - **Position** is exact and cheap, and is what makes an anchor survive a
 *     re-render, a register toggle, or a highlight drawn over the top.
 *   - **Quote** is what makes it survive the text itself changing — a
 *     re-parse, a corrected transcription, the employer tabling a redraft.
 *
 * Position is tried first and only accepted if the quote still agrees, so the
 * cheap path can never place a mark on words it does not match.
 */
/**
 * Context kept either side of a selection.
 *
 * Enough to disambiguate a repeated phrase — and "the Employer must" occurs
 * dozens of times in this agreement — without storing so much of the clause
 * that the anchor is really a copy of it.
 */
export const CONTEXT_LENGTH = 32;
/** Builds an anchor for `[start, end)` of `text`. */
export function anchorFor(text, start, end) {
    const source = String(text ?? '');
    if (!Number.isInteger(start) || !Number.isInteger(end))
        return null;
    if (start < 0 || end > source.length || end <= start)
        return null;
    return {
        exact: source.slice(start, end),
        prefix: source.slice(Math.max(0, start - CONTEXT_LENGTH), start),
        suffix: source.slice(end, Math.min(source.length, end + CONTEXT_LENGTH)),
        start,
        end,
    };
}
/**
 * How much of two strings match, reading outwards from the join.
 *
 * Used to score candidate positions of a repeated quote: the one whose
 * surrounding text agrees with the stored context for longest is the one the
 * annotator meant.
 */
function sharedSuffix(a, b) {
    let n = 0;
    while (n < a.length && n < b.length && a[a.length - 1 - n] === b[b.length - 1 - n])
        n++;
    return n;
}
function sharedPrefix(a, b) {
    let n = 0;
    while (n < a.length && n < b.length && a[n] === b[n])
        n++;
    return n;
}
/**
 * Places an anchor in `text`, or returns null if it cannot be placed.
 *
 * Null is a real answer and the caller must render it — an orphaned mark is
 * information (the words it was attached to are gone, which for a bargaining
 * document is exactly the sort of change someone needs to notice) whereas a
 * silently relocated mark is a lie.
 */
export function placeAnchor(text, anchor) {
    const source = String(text ?? '');
    const { exact } = anchor;
    if (!exact)
        return null;
    // The cheap path: the stored offsets still hold the stored words.
    if (source.slice(anchor.start, anchor.end) === exact) {
        return { start: anchor.start, end: anchor.end, how: 'exact' };
    }
    // Otherwise find every occurrence and pick the one whose neighbourhood best
    // matches the context that was captured with it.
    let best = null;
    for (let from = source.indexOf(exact); from !== -1; from = source.indexOf(exact, from + 1)) {
        const before = source.slice(Math.max(0, from - CONTEXT_LENGTH), from);
        const after = source.slice(from + exact.length, from + exact.length + CONTEXT_LENGTH);
        const score = sharedSuffix(before, anchor.prefix ?? '') + sharedPrefix(after, anchor.suffix ?? '');
        // Ties break towards the original position, which is the right guess when
        // the same phrase appears twice with identical surroundings.
        if (!best ||
            score > best.score ||
            (score === best.score &&
                Math.abs(from - anchor.start) < Math.abs(best.index - anchor.start))) {
            best = { index: from, score };
        }
    }
    if (!best)
        return null;
    return { start: best.index, end: best.index + exact.length, how: 'moved' };
}
/**
 * Splits `text` into segments at every anchor boundary.
 *
 * Overlapping marks are the normal case rather than the exception — two people
 * highlighting the same sentence from different starting points is what the
 * heat overlay is made of — so this cuts at every boundary and reports which
 * marks cover each piece, instead of nesting elements that would have to be
 * balanced.
 */
export function segment(text, placed) {
    const source = String(text ?? '');
    if (source.length === 0)
        return [];
    const inRange = placed.filter((p) => p.start >= 0 && p.end <= source.length && p.end > p.start);
    if (inRange.length === 0)
        return [{ text: source, start: 0, marks: [] }];
    const cuts = new Set([0, source.length]);
    for (const p of inRange) {
        cuts.add(p.start);
        cuts.add(p.end);
    }
    const bounds = [...cuts].sort((a, b) => a - b);
    const segments = [];
    for (let i = 0; i < bounds.length - 1; i++) {
        const start = bounds[i];
        const end = bounds[i + 1];
        if (end <= start)
            continue;
        segments.push({
            text: source.slice(start, end),
            start,
            // Longest span first, so the outermost mark decides the base styling.
            marks: inRange
                .filter((p) => p.start <= start && p.end >= end)
                .sort((a, b) => b.end - b.start - (a.end - a.start))
                .map((p) => p.id),
        });
    }
    return segments;
}
//# sourceMappingURL=anchors.js.map